'use client';

import { Button } from '@florencelabs/button';
import { MaterialSymbol, MaterialSymbolName } from '@florencelabs/icons';
import { useState, ReactElement, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Selection } from 'react-stately';
import { useToggle } from 'usehooks-ts';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import {
  Menu,
  MenuItem,
  MenuSection,
  MenuSectionItem,
  MenuHeader,
  MenuPressable,
  MenuKey,
} from '@provider-directory/components/menu/menu';
import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import { useSearch } from '@provider-directory/contexts/ProviderSearchContext';
import {
  SortOption,
  SingleSortItem,
  SortItem,
  SectionSortItem,
} from '@provider-directory/types/search.model';

// Sort select specific values
interface SelectSortItem extends SortItem {
  isDisabled?: boolean;
}

export interface SortSection {
  type: 'section';
  section: string;
  items: SectionSortItem<SelectSortItem>[];
}

type ClassifiedSortItem =
  | SingleSortItem<SelectSortItem>
  | SectionSortItem<SelectSortItem>;

type GroupedClassifiedOption = SortSection | SingleSortItem<SelectSortItem>;

const findOption = (
  key: string,
  options: SortOption[],
): SortOption | undefined => {
  return options.find((option) => option.value === key);
};

const groupSections = (
  acc: GroupedClassifiedOption[],
  option: ClassifiedSortItem,
): GroupedClassifiedOption[] => {
  if (option.type === 'section') {
    const section = acc.find(
      (item) => item.type === 'section' && item.section === option.section,
    ) as SortSection | undefined;

    // for section options, we group items into an array
    if (section) {
      section.items.push(option);
    } else {
      acc.push({
        type: 'section',
        section: option.section,
        items: [option],
      });
    }
  } else {
    acc.push(option);
  }

  return acc;
};

const SortByButton = ({
  selectedLabel,
  iconName,
}: {
  selectedLabel: string;
  iconName: MaterialSymbolName;
}) => {
  const { t } = useTranslation();
  return (
    <MenuPressable>
      <Button
        variant={'systemLink'}
        className={
          'flex flex-row items-center justify-center !gap-0 !p-0 outline outline-0'
        }
        testId={'sort-by-selector-btn'}
      >
        <span
          className={
            'hidden text-body-small-semibold text-neutral-600 tablet:block desktop:text-body-medium-semibold'
          }
        >
          {t('searchFilters.sortBy')}
        </span>
        <span
          className={
            'ml-1 text-body-small-semibold desktop:text-body-medium-semibold'
          }
        >
          {t(selectedLabel)}
        </span>
        <MaterialSymbol name={iconName} />
      </Button>
    </MenuPressable>
  );
};

export const SortSelect = (): ReactElement => {
  const { t } = useTranslation();
  const { search, searchCriteria, isLocationSelected } = useSearch();
  const { config } = useOrgContext();
  const analytics = useAnalytics();
  const options = config.sorting.options;
  const selectedValue = searchCriteria.sort ?? config.sorting.default;

  const [isOpen, onOpenChange] = useToggle(false);
  const iconName = isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
  const defaultItem = findOption(config.sorting.default, options);

  if (!defaultItem) {
    throw new Error('Default sorting option not found in options');
  }

  const [selected, setSelected] = useState<Selection>(
    new Set([findOption(selectedValue, options)?.value ?? defaultItem.value]),
  );

  const groupedOptions = useMemo(() => {
    // only marks distance as disabled when no distance given
    const classifiedOptions = options.map((option) => {
      return {
        ...option,
        isDisabled: option.value === 'distance' && !isLocationSelected,
      };
    });

    return classifiedOptions.reduce(groupSections, []);
  }, [options, isLocationSelected]);

  const selectedOption = findOption(selectedValue, options) ?? defaultItem;
  const selectedLabel = `${
    selectedOption.type === 'section' ? t(selectedOption.section) + ' ' : ''
  }${t(selectedOption.label)}`;

  // Resolve raw sort value (e.g. "-name") to a readable label (e.g. "Name Z-A")
  const buildSortLabel = (value: string): string => {
    const option = findOption(value, options);
    if (!option) return value;
    const prefix = option.type === 'section' ? t(option.section) + ' ' : '';
    return `${prefix}${t(option.label)}`;
  };

  const onChangeSort = (key: MenuKey) => {
    const newValue = String(key);
    analytics.changedSortEvent({
      sortValue: buildSortLabel(newValue),
      previousSortValue: buildSortLabel(selectedValue),
      linkContextPage: 'SearchResults',
      searchCriteria,
    });
    search({ key: 'sort', option: newValue });
  };

  useEffect(() => {
    const option = findOption(selectedValue, options) ?? defaultItem;
    const newValue = option.value;

    const currentValue = Array.from(selected)[0];
    if (currentValue !== newValue) {
      setSelected(new Set([newValue]));
    }
  }, [selectedValue, options, defaultItem, selected]);

  return (
    <Menu
      TriggerComponent={SortByButton}
      triggerComponentProps={{
        selectedLabel,
        iconName,
      }}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onAction={onChangeSort}
      selectionMode={'single'}
      selectedKeys={selected}
      onSelectionChange={setSelected}
      heading={t('searchFilters.sortBy')}
      items={groupedOptions}
      testIdPrefix={'sort-by'}
      aria-label={'Select sort by'}
    >
      {groupedOptions.map((option) =>
        option.type === 'item' ? (
          <MenuItem
            id={option.value}
            key={option.value}
            isDisabled={option.isDisabled}
            data-testid={`sort-by-option-${option.value
              .replace(/\s+/g, '')
              .toLowerCase()}`}
          >
            {t(option.label)}
          </MenuItem>
        ) : (
          <MenuSection
            id={option.section}
            key={option.section}
            className={'text-body-small-semibold'}
          >
            <MenuHeader label={t(option.section)} />
            {option.items.map((child) => (
              <MenuSectionItem
                id={child.value}
                key={child.value}
                isDisabled={child.isDisabled}
                data-testid={`sort-by-option-${child.value
                  .replace(/\s+/g, '')
                  .toLowerCase()}`}
              >
                {t(child.label)}
              </MenuSectionItem>
            ))}
          </MenuSection>
        ),
      )}
    </Menu>
  );
};
